import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import './AssuranceReport.css';
import LearningGoalsModal from './LearningGoalsModal';
import CourseAlignmentOverview from './CourseAlignmentOverview';
import Recommendations from './Recommendations';
import MicroCredentials from './MicroCredentials';
import { getAllTemplates, getAlignmentScore, exportExcelReport, getEvaluateResult, startAssessmentByTemplate, getSurveyAnalyses, getCareerEvaluationResult, getRecommendedCourseDetails } from '../services/api';
import IndirectMethodSummary from './IndirectMethodSummary';
import CourseManagement from './CourseManagement';

import { useSelectedTemplate } from '../context/SelectedTemplateContext';
import { useLocation, useNavigate } from 'react-router-dom';
import { useNotification, NotificationContainer } from './Notification';
import { useParams } from 'react-router-dom';

const urlParams = new URLSearchParams(window.location.search);

const AssuranceReport = ({ 
  onBack, 
  programName, 
  courseList = [], 
  school = urlParams.get('school') || 'MARYLAND', 
  sidebarExpanded, 
  isCourse = false,
  returnedFromEvidence = false,
  returnedFromReportGeneration = false,
  fullAlignmentData = null,
  originalTableData = null,
  originalData = null,
  initialTab = null
}) => {
  // Use programName from props, or fall back to a default only if truly undefined
  const displayProgramName = programName || "Program Assessment";
  console.log("AssuranceReport rendering", { 
    programName: displayProgramName, 
    courseCount: courseList.length,
    initialTab,
    isCourse,
    returnedFromReportGeneration
  });
  
  const location = useLocation();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState(initialTab || 'Curriculum Mapping');
  const [editMode, setEditMode] = useState(false);
  
  // Debug editMode state
  useEffect(() => {
    console.log("editMode changed:", editMode);
  }, [editMode]);
  const [academicYear, setAcademicYear] = useState('2025 Spring');
  const [showYearDropdown, setShowYearDropdown] = useState(false);
  const [showExportDropdown, setShowExportDropdown] = useState(false);
  const [showFileTypeDropdown, setShowFileTypeDropdown] = useState(false);
  const [showLearningGoalsModal, setShowLearningGoalsModal] = useState(false);
  const [fileType, setFileType] = useState('Excel');
  const [exportSelections, setExportSelections] = useState({
    all: true,
    curriculumMapping: false,
    courseAlignment: false,
    recommendation: false
  });
  const [allLearningGoals, setAllLearningGoals] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [templateData, setTemplateData] = useState(null);
  const [alignmentData, setAlignmentData] = useState(fullAlignmentData);
  const [indirectScores, setIndirectScores] = useState({});
  const [alignmentLoading, setAlignmentLoading] = useState(false);
  const [coursesData, setCoursesData] = useState([]);
  const [isReturnedFromEvidence, setIsReturnedFromEvidence] = useState(returnedFromEvidence);
  const [shouldFetchTemplate, setShouldFetchTemplate] = useState(false);
  const [surveys, setSurveys] = useState([]);
  const [selectedSurvey, setSelectedSurvey] = useState(null);
  const [surveysLoading, setSurveysLoading] = useState(false);
  
  // Career Assessment state
  const [careerData, setCareerData] = useState([]);
  const [careerLoading, setCareerLoading] = useState(false);
  const [careerError, setCareerError] = useState(null);
  const [selectedJob, setSelectedJob] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [jobsPerPage] = useState(5);
  const [showCourseManagement, setShowCourseManagement] = useState(false);
  const [currentSkillsExpanded, setCurrentSkillsExpanded] = useState(false);
  const [missingSkillsExpanded, setMissingSkillsExpanded] = useState(false);
  const [boostPercentage, setBoostPercentage] = useState(10); // Default to 10%
  
  // PDF Preview Modal state
  const [showPdfModal, setShowPdfModal] = useState(false);
  const [selectedPdfUrl, setSelectedPdfUrl] = useState('');
  const [selectedPdfTitle, setSelectedPdfTitle] = useState('');
  const [pdfPreviewError, setPdfPreviewError] = useState(false);
  const [pdfBlobUrl, setPdfBlobUrl] = useState('');
  const [isLoadingPdf, setIsLoadingPdf] = useState(false);
  
  // Refs for measuring skills rows
  const currentSkillsRef = useRef(null);
  const missingSkillsRef = useRef(null);
  const [currentSkillsRows, setCurrentSkillsRows] = useState(0);
  const [missingSkillsRows, setMissingSkillsRows] = useState(0);
  
  const exportRef = useRef(null);
  const fileTypeRef = useRef(null);

  const { selectedTemplate, setSelectedTemplate } = useSelectedTemplate();
  
  // Add notification system
  const { notifications, addNotification, removeNotification } = useNotification();
  
  // Template validation helper
  const isTemplateComplete = (template) => {
    return template && template.LG && Array.isArray(template.LG) && template.LG.length > 0;
  };
  
  // Cache the tab components to prevent remounting
  const tabComponentsCache = useRef({});
  
  // Create a hash of the current mapping state to detect changes
  const mappingHash = useMemo(() => {
    return coursesData.map(course => 
      course.mapping.map(m => m.checked ? 1 : 0).join('')
    ).join('|');
  }, [coursesData]);
  
  // Track if mapping has changed since last fetch
  const lastFetchedMappingHash = useRef(mappingHash);
  const fetchTimeoutRef = useRef(null);
  
  // Generate a storage key based on programName and school
  const getStorageKey = () => {
    return `learning_goals_selection_${displayProgramName.replace(/\s+/g, '_')}_${school}`;
  };
  
  // Generate a storage key for template selection
  const getTemplateStorageKey = () => {
    return `selected_template_${displayProgramName.replace(/\s+/g, '_')}_${school}`;
  };

  // Check if we should show the modal on first load
  useEffect(() => {
    // Skip if returning from evidence page
    if (isReturnedFromEvidence) {
      return;
    }
    
    // The modal will be shown by the template fetching logic if needed
    // This useEffect is now mainly for handling the returned from evidence case
  }, [isReturnedFromEvidence]);

  const handleSelectTemplate = (template) => {
    if (template && template.id !== selectedTemplate?.id) {
      // Check if template is disabled (this will be called from within the modal where we have access to the evaluation results)
      // Since we're calling this from outside the modal context, we'll need to validate differently
      // For now, we'll just proceed with the selection and let the modal handle the disabled state
      
      // Update selected template
      setSelectedTemplate(template);
      
      // Save the selected template to localStorage
      const templateStorageKey = getTemplateStorageKey();
      localStorage.setItem(templateStorageKey, JSON.stringify(template));
      
      // Reset alignment data since it will be different for the new template
      setAlignmentData(null);
      
      // Clear any cached data in sessionStorage
      sessionStorage.removeItem('return_from_evidence_state');
      sessionStorage.removeItem('course_alignment_state');
      
      // Reset isReturnedFromEvidence flag to ensure fresh data is fetched
      setIsReturnedFromEvidence(false);
      
      // Force alignment data to be refreshed if on the Course Alignment tab
      if (activeTab === 'Course Alignment Overview') {
        setAlignmentLoading(true);
        // We'll fetch alignment data in the useEffect that watches for selectedTemplate changes
      }
      
      // Show a success message
      const successMessage = document.createElement('div');
      successMessage.className = 'template-selection-success';
      successMessage.textContent = `Template "${template.name}" selected successfully`;
      document.body.appendChild(successMessage);
      
      // Remove the success message after 3 seconds
      setTimeout(() => {
        if (successMessage.parentNode) {
          successMessage.parentNode.removeChild(successMessage);
        }
      }, 3000);
    }
  };
  
  // Format learning goal ID properly (e.g., convert "11" to "1.1", "12" to "1.2")
  const formatLearningGoalId = (id) => {
    // If it's already in the correct format (contains a dot), return as is
    if (String(id).includes('.')) {
      return id;
    }
    
    // If it's a string with 2 or more digits, insert a dot after the first digit
    const idStr = String(id);
    if (idStr.length >= 2) {
      return `${idStr[0]}.${idStr.substring(1)}`;
    }
    
    // Otherwise, return as is
    return id;
  };
  
  // Fetch surveys for the current school
  const fetchSurveys = useCallback(async () => {
    try {
      setSurveysLoading(true);
      const surveyData = await getSurveyAnalyses(school);
      
      // Filter to only show completed surveys
      const completedSurveys = (surveyData || []).filter(survey => survey.status === 'completed');
      setSurveys(completedSurveys);
      
      // Don't auto-select any survey - let user choose or leave it empty
    } catch (error) {
      console.error('Error fetching surveys:', error);
      setSurveys([]);
    } finally {
      setSurveysLoading(false);
    }
  }, [school]);
  
  // Calculate indirect scores from selected survey
  const calculateIndirectScoresFromSurvey = useCallback(() => {
    if (!selectedSurvey || !alignmentData) {
      return {};
    }
    
    // Convert the overall_average from 0-5 scale to 0-100 percentage
    const surveyScore = ((selectedSurvey.overall_average || selectedSurvey.analysis_results?.overall_summary?.overall_average || 0) / 5 * 100).toFixed(1);
    
    // Apply this score to all courses in the alignment data
    const calculatedIndirectScores = {};
    Object.keys(alignmentData).forEach(courseName => {
      calculatedIndirectScores[courseName] = surveyScore;
    });
    
    return calculatedIndirectScores;
  }, [selectedSurvey, alignmentData]);
  
  // Update indirect scores when survey selection changes
  useEffect(() => {
    if (selectedSurvey) {
      const newIndirectScores = calculateIndirectScoresFromSurvey();
      setIndirectScores(newIndirectScores);
    } else {
      // Clear indirect scores if no survey is selected
      setIndirectScores({});
    }
  }, [selectedSurvey, calculateIndirectScoresFromSurvey]);
  
  // Fetch alignment scores based on selected learning goals
  const fetchAlignmentScores = useCallback(async () => {
    // Skip if we don't have the necessary data
    if (!coursesData || coursesData.length === 0 || !allLearningGoals || allLearningGoals.length === 0 || !selectedTemplate) {
      console.log("Skipping fetchAlignmentScores - missing required data:", { 
        coursesDataLength: coursesData?.length || 0, 
        allLearningGoalsLength: allLearningGoals?.length || 0, 
        hasSelectedTemplate: !!selectedTemplate 
      });
      return;
    }
    
    // Always fetch when explicitly requested to ensure fresh data
    console.log("Fetching alignment scores...", { mappingHash, lastFetchedHash: lastFetchedMappingHash.current });
    setAlignmentLoading(true);
      
    try {
      // Build details object for API request
      const details = {};
      
      // For each course, find which learning objectives are checked
      coursesData.forEach(course => {
        const selectedLOs = [];
        let currentIndex = 0;
        
        // Process all learning goals to find checked objectives
        for (let i = 0; i < allLearningGoals.length; i++) {
          const lg = allLearningGoals[i];
          for (let j = 0; j < lg.objectives.length; j++) {
            if (course.mapping[currentIndex]?.checked) {
              // Format the objective value based on its format
              let objectiveValue;
              const objective = lg.objectives[j];
              
              // If it starts with "LG", extract the numeric part
              if (typeof objective === 'string' && objective.startsWith('LG')) {
                objectiveValue = objective.replace(/\D/g, '');
        } else {
                // Otherwise use as is (likely already a number or formatted correctly)
                objectiveValue = parseFloat(objective) || objective;
              }
              
              // Format the learning goal ID properly (e.g., convert 11 to 1.1)
              objectiveValue = formatLearningGoalId(objectiveValue);
              
              selectedLOs.push(objectiveValue);
            }
            currentIndex++;
          }
        }
        
        // Only add courses with selected learning objectives
        if (selectedLOs.length > 0) {
          details[course.name] = selectedLOs;
        }
      });
      
      // Only make API call if there are courses with selected learning objectives
      if (Object.keys(details).length > 0 && selectedTemplate) {
        // Use the selected template ID for the API call

        // Use the actual programName for survey matching
        const programCode = programName || 'BSM';
        
        const response = await getAlignmentScore(selectedTemplate.id || 1, details, school, programCode);
        
        // Handle the new response structure with alignmentData and indirectScores
        if (response && response.alignmentData) {
          setAlignmentData(response.alignmentData);
          // Store indirect scores separately if needed
          if (response.indirectScores) {
            // console.log("Indirect scores received from API:", response.indirectScores);
            setIndirectScores(response.indirectScores);
          } else {
            // console.log("No indirectScores in API response");
            setIndirectScores({});
          }
        } else {
          // Fallback for old response format
          // console.log("Using fallback for old response format");
          setAlignmentData(response);
          setIndirectScores({});
        }
        
        // Update the last fetched mapping hash to track successful fetches
        lastFetchedMappingHash.current = mappingHash;
      }
    } catch (err) {

      console.error('Error fetching alignment scores:', err);
      setError('Failed to load alignment data');
    } finally {
      setAlignmentLoading(false);
    }
  }, [coursesData, allLearningGoals, selectedTemplate, mappingHash]);

  // Fetch career evaluation data
  const fetchCareerData = useCallback(async () => {
    if (!displayProgramName || !school) return;
    
    setCareerLoading(true);
    setCareerError(null);
    
    try {
      const data = await getCareerEvaluationResult(displayProgramName, school);
      setCareerData(data || []);
      
      // Set the first job as selected by default
      if (data && data.length > 0) {
        setSelectedJob(data[0]);
      }
    } catch (err) {
      console.error('Error fetching career data:', err);
      setCareerError('Failed to load career data');
    } finally {
      setCareerLoading(false);
    }
  }, [displayProgramName, school]);

  // Calculate boost percentage for selected job using recommended course details
  const calculateJobBoostPercentage = useCallback(async () => {
    if (!selectedJob || !selectedTemplate?.id || !displayProgramName) {
      setBoostPercentage(10); // Default value
      return;
    }
    
    try {
      const response = await getRecommendedCourseDetails(selectedTemplate.id, displayProgramName);
      console.log('Response from getRecommendedCourseDetails:', response);
      console.log('Selected job ID:', selectedJob.id);
      
      if (response && response.courses && response.courses.length > 0) {
        // Find courses that are relevant to the selected job by matching job_title
        const jobRelevantCourses = response.courses.filter(course => 
          course.jobs && course.jobs.some(job => job.job_title === selectedJob.job_title)
        );
        
        console.log('Job relevant courses:', jobRelevantCourses);
        
        if (jobRelevantCourses.length > 0) {
          // Calculate average overall_likelihood_percentage from the course's overall_likelihood_percentage
          const totalPercentage = jobRelevantCourses.reduce((sum, course) => {
            console.log('Course title:', course.course_title);
            console.log('Course overall_likelihood_percentage:', course.overall_likelihood_percentage);
            return sum + (course.overall_likelihood_percentage || 0);
          }, 0);
          
          const averagePercentage = Math.round(totalPercentage / jobRelevantCourses.length);
          console.log('Calculated average percentage:', averagePercentage);
          setBoostPercentage(averagePercentage);
        } else {
          console.log('No relevant courses found for job title:', selectedJob.job_title);
          setBoostPercentage(10); // Default if no relevant courses
        }
      } else {
        console.log('No courses in response');
        setBoostPercentage(10); // Default if no courses
      }
    } catch (error) {
      console.error('Error calculating job boost percentage:', error);
      setBoostPercentage(10); // Default on error
    }
  }, [selectedJob, selectedTemplate?.id, displayProgramName]);

  // Helper function to calculate match percentage
  const calculateMatchPercentage = (requiredSkills, missingSkills) => {
    const totalSkills = requiredSkills.length;
    const matchedSkills = totalSkills - missingSkills.length;
    return Math.round((matchedSkills / totalSkills) * 100);
  };

  // Helper function to format salary
  const formatSalary = (salary) => {
    if (salary >= 1000000) {
      return `$${(salary / 1000000).toFixed(1)}M`;
    } else if (salary >= 1000) {
      return `$${(salary / 1000).toFixed(0)}K`;
    }
    return `$${salary.toFixed(0)}`;
  };

  // Helper function to format job growth
  const formatJobGrowth = (growth) => {
    return growth > 0 ? `+${growth}%` : `${growth}%`;
  };

  // Handle job selection
  const handleJobSelect = (job) => {
    setSelectedJob(job);
    setCurrentSkillsExpanded(false); // Reset skills expansion when selecting new job
    setMissingSkillsExpanded(false);
    setCurrentSkillsRows(0); // Reset row counts for new job
    setMissingSkillsRows(0);
    
    // Debug logging for skills data
    console.log('Selected job:', job.title);
    console.log('Required skills:', job.required_skills);
    console.log('Missing skills:', job.missing_skills);
    const currentSkills = job.required_skills.filter(skill => !job.missing_skills.includes(skill));
    console.log('Calculated current skills:', currentSkills);
    console.log('Current skills count:', currentSkills.length);
    console.log('Missing skills count:', job.missing_skills.length);
  };

  // Fetch PDF and convert to blob URL
  const fetchPdfAsBlob = async (pdfUrl) => {
    try {
      setIsLoadingPdf(true);
      
      const response = await fetch(pdfUrl);
      if (!response.ok) {
        throw new Error(`Failed to fetch PDF: ${response.status}`);
      }
      
      const blob = await response.blob();
      const blobUrl = window.URL.createObjectURL(blob);
      
      console.log('PDF converted to blob URL:', blobUrl);
      return blobUrl;
    } catch (error) {
      console.error('Error fetching PDF as blob:', error);
      throw error;
    } finally {
      setIsLoadingPdf(false);
    }
  };

  // Handle PDF preview
  const handleViewSyllabus = async (syllabusUrl, courseTitle, event) => {
    console.log('handleViewSyllabus called with:', { syllabusUrl, courseTitle });
    
    // Prevent any default behavior
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }
    
    if (syllabusUrl && syllabusUrl !== 'xxx') {
      console.log('Opening PDF modal for:', syllabusUrl);
      setSelectedPdfUrl(syllabusUrl);
      setSelectedPdfTitle(courseTitle);
      setShowPdfModal(true);
      setPdfPreviewError(false);
      
      // Fetch PDF as blob URL for preview
      try {
        const blobUrl = await fetchPdfAsBlob(syllabusUrl);
        setPdfBlobUrl(blobUrl);
      } catch (error) {
        console.error('Failed to load PDF preview:', error);
        setPdfPreviewError(true);
      }
    } else {
      console.log('Syllabus not available');
      addNotification('Syllabus not available for this course', 'warning');
    }
  };

  // Close PDF modal
  const handleClosePdfModal = () => {
    setShowPdfModal(false);
    setSelectedPdfUrl('');
    setSelectedPdfTitle('');
    setPdfPreviewError(false);
    
    // Clean up blob URL to prevent memory leaks
    if (pdfBlobUrl) {
      window.URL.revokeObjectURL(pdfBlobUrl);
      setPdfBlobUrl('');
    }
  };

  // Handle PDF download
  const handleDownloadPdf = async () => {
    if (!selectedPdfUrl) {
      alert('No PDF file available for download');
      return;
    }

    try {
      let downloadUrl;
      
      // Fetch the PDF from URL to create a blob
      console.log('Fetching PDF from URL for download:', selectedPdfUrl);
      const response = await fetch(selectedPdfUrl);
      if (!response.ok) {
        throw new Error(`Failed to fetch PDF: ${response.status}`);
      }
      const blob = await response.blob();
      downloadUrl = window.URL.createObjectURL(blob);
      
      // Create download link
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = `${selectedPdfTitle}.pdf`;
      link.style.display = 'none';
      
      // Add to DOM, click, and remove
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      // Clean up the blob URL after a short delay
      setTimeout(() => {
        window.URL.revokeObjectURL(downloadUrl);
      }, 100);
      
    } catch (error) {
      console.error('Download failed:', error);
      alert('Download failed. Please try again.');
    }
  };

  // Handle iframe load error
  const handleIframeError = () => {
    const fallback = document.querySelector('.pdf-fallback');
    const iframe = document.querySelector('.pdf-iframe');
    if (fallback && iframe) {
      iframe.style.display = 'none';
      fallback.style.display = 'flex';
    }
  };

  // Handle skills expand/collapse for each column separately
  const handleCurrentSkillsToggle = () => {
    setCurrentSkillsExpanded(!currentSkillsExpanded);
    // Reset row count to force re-measurement
    setCurrentSkillsRows(0);
  };

  const handleMissingSkillsToggle = () => {
    setMissingSkillsExpanded(!missingSkillsExpanded);
    // Reset row count to force re-measurement
    setMissingSkillsRows(0);
  };

  // Get skills to display (limited to 3 rows when collapsed)
  const getSkillsToDisplay = (skills, isCurrentSkills = true) => {
    const isExpanded = isCurrentSkills ? currentSkillsExpanded : missingSkillsExpanded;
    if (isExpanded) return skills;
    
    const rowCount = isCurrentSkills ? currentSkillsRows : missingSkillsRows;
    console.log(`getSkillsToDisplay: isExpanded=${isExpanded}, rowCount=${rowCount}, totalSkills=${skills.length}`);
    
    // If rowCount is 0 (initial state), show all skills until measurement is complete
    if (rowCount === 0) {
      console.log(`Row count is 0, showing all skills until measurement`);
      return skills;
    }
    
    // If 3 or fewer rows, show all skills
    if (rowCount <= 3) return skills;
    
    // If more than 3 rows, we need to limit to roughly 3 rows worth
    // Use a more conservative approach: show about 75% of skills to ensure we stay within 3 rows
    const skillsPerRow = skills.length / rowCount;
    const maxSkills = Math.floor(skillsPerRow * 3);
    const limitedSkills = skills.slice(0, Math.max(1, maxSkills)); // Always show at least 1 skill
    console.log(`Limiting skills: ${skills.length} -> ${limitedSkills.length} (estimated ${maxSkills} for 3 rows)`);
    return limitedSkills;
  };

  // Get remaining skills count
  const getRemainingSkillsCount = (skills, isCurrentSkills = true) => {
    const isExpanded = isCurrentSkills ? currentSkillsExpanded : missingSkillsExpanded;
    if (isExpanded) return 0;
    
    const rowCount = isCurrentSkills ? currentSkillsRows : missingSkillsRows;
    
    // If rowCount is 0 (initial state), no remaining skills until measurement is complete
    if (rowCount === 0) {
      console.log(`Row count is 0, returning 0 remaining skills until measurement`);
      return 0;
    }
    
    // If 3 or fewer rows, no remaining skills
    if (rowCount <= 3) {
      console.log(`getRemainingSkillsCount: rowCount=${rowCount} <= 3, returning 0`);
      return 0;
    }
    
    // If more than 3 rows, calculate remaining skills using the same logic as getSkillsToDisplay
    const skillsPerRow = skills.length / rowCount;
    const maxSkills = Math.floor(skillsPerRow * 3);
    const shownSkills = Math.max(1, maxSkills);
    const remaining = Math.max(0, skills.length - shownSkills);
    console.log(`getRemainingSkillsCount: rowCount=${rowCount}, skillsPerRow=${skillsPerRow.toFixed(1)}, maxSkills=${maxSkills}, shownSkills=${shownSkills}, remaining=${remaining}`);
    return remaining;
  };

  // Measure rows for both columns
  useEffect(() => {
    if (selectedJob) {
      // Use requestAnimationFrame to ensure DOM is updated after next paint
      requestAnimationFrame(() => {
        // Measure current skills column
        if (currentSkillsRef.current) {
          const currentSkillTags = currentSkillsRef.current.querySelectorAll('.skill-tag:not(.more-skills):not(.collapse-skills)');
          if (currentSkillTags.length > 0) {
            const rows = new Set();
            currentSkillTags.forEach(tag => {
              rows.add(tag.offsetTop);
            });
            const rowCount = rows.size;
            console.log('Current skills row measurement:');
            console.log('- Skills count:', currentSkillTags.length);
            console.log('- Row count:', rowCount);
            console.log('- Unique offsetTops:', Array.from(rows));
            setCurrentSkillsRows(rowCount);
          } else {
            console.log('Current skills: No skill tags found');
            setCurrentSkillsRows(0);
          }
        }
        
        // Measure missing skills column
        if (missingSkillsRef.current) {
          const missingSkillTags = missingSkillsRef.current.querySelectorAll('.skill-tag:not(.more-skills):not(.collapse-skills)');
          if (missingSkillTags.length > 0) {
            const rows = new Set();
            missingSkillTags.forEach(tag => {
              rows.add(tag.offsetTop);
            });
            const rowCount = rows.size;
            console.log('Missing skills row measurement:');
            console.log('- Skills count:', missingSkillTags.length);
            console.log('- Row count:', rowCount);
            console.log('- Unique offsetTops:', Array.from(rows));
            setMissingSkillsRows(rowCount);
          } else {
            console.log('Missing skills: No skill tags found');
            setMissingSkillsRows(0);
          }
        }
      });
    }
  }, [selectedJob, currentSkillsExpanded, missingSkillsExpanded]);

  // Handle tab change with optimization to prevent unnecessary fetches
  const handleTabChange = useCallback((tabName) => {
    //console.log(`Changing tab to: ${tabName}`);
    // Only process if it's a different tab
    if (tabName === activeTab) return;
    
    const previousTab = activeTab;
    setActiveTab(tabName);
    
    // Update URL with tab name while preserving school parameter
    const urlParams = new URLSearchParams(window.location.search);
    const school = urlParams.get('school') || 'MARYLAND';
    
    // Create URL-friendly tab name
    const tabParam = tabName.toLowerCase().replace(/ /g, '-');
    
    // Update URL without page reload
    const newUrl = `${window.location.pathname}?school=${school}&tab=${tabParam}`;
    window.history.pushState({ path: newUrl }, '', newUrl);
    
    // If switching to Course Alignment Overview from another tab, check if we need fresh data
    if (tabName === 'Course Alignment Overview' && previousTab !== 'Course Alignment Overview') {
      // Clear the returned from evidence flag to allow fresh fetch if needed
      setIsReturnedFromEvidence(false);
      
      // Only fetch if mapping has actually changed since last fetch or we don't have data
      const mappingChanged = lastFetchedMappingHash.current !== mappingHash;
      const hasNoData = !alignmentData;
      
      if ((mappingChanged || hasNoData) && coursesData.length > 0 && selectedTemplate) {
        // Clear existing alignment data to ensure refetch
        setAlignmentData(null);
        fetchAlignmentScores();
      }
    }
    
    // If switching to Career Assessment tab, fetch career data and recommended course details
    if (tabName === 'Career Assessment' && previousTab !== 'Career Assessment') {
      fetchCareerData();
      // Also fetch recommended course details for boost percentage calculation
      if (selectedTemplate?.id && displayProgramName) {
        getRecommendedCourseDetails(selectedTemplate.id, displayProgramName)
          .then(response => {
            console.log('Fetched recommended course details for Career Assessment:', response);
          })
          .catch(error => {
            console.error('Error fetching recommended course details:', error);
          });
      }
    }
  }, [activeTab, mappingHash, alignmentData, coursesData.length, selectedTemplate, fetchCareerData]);

  // Debounced effect for mapping changes when on Course Alignment Overview tab
  useEffect(() => {
    // Clear any existing timeout
    if (fetchTimeoutRef.current) {
      clearTimeout(fetchTimeoutRef.current);
    }
    
    // Only react to mapping changes when on Course Alignment Overview tab
    // Skip if we don't have the required data or if returning from evidence/report generation
    if (activeTab === 'Course Alignment Overview' && 
        selectedTemplate && 
        allLearningGoals.length > 0 &&
        coursesData.length > 0 &&
        !alignmentLoading &&
        !isReturnedFromEvidence) {
      
      // Check if mapping has actually changed since last successful fetch
      const mappingChanged = lastFetchedMappingHash.current !== mappingHash;
      
      if (mappingChanged) {
        // Debounce the fetch to avoid too many API calls
        fetchTimeoutRef.current = setTimeout(() => {
          fetchAlignmentScores();
        }, 1000); // Wait 1 second after last change
      } else if (!alignmentData && !alignmentLoading) {
        // If no data exists, fetch immediately
        fetchAlignmentScores();
      }
    }
    
    // Cleanup timeout on unmount
    return () => {
      if (fetchTimeoutRef.current) {
        clearTimeout(fetchTimeoutRef.current);
      }
    };
  }, [
    activeTab, 
    selectedTemplate, 
    allLearningGoals.length, 
    mappingHash, // Track mapping changes to trigger refetch when selections change
    alignmentData, 
    alignmentLoading, 
    isReturnedFromEvidence,
    coursesData.length
  ]);
  
  // Use selectedTemplate for curriculum mapping and review modal
  useEffect(() => {
    console.log("Template useEffect triggered:", { 
      hasSelectedTemplate: !!selectedTemplate, 
      templateId: selectedTemplate?.id,
      hasLG: !!selectedTemplate?.LG,
      lgLength: selectedTemplate?.LG?.length || 0
    });
    
    if (selectedTemplate) {
      setTemplateData(selectedTemplate);
      if (selectedTemplate.LG) {
        // Process all learning goals (both main and sub) into a flat structure
        const flatLearningGoals = [];
        
        selectedTemplate.LG.forEach(lg => {
          // Add main learning goal
          flatLearningGoals.push({
            id: lg.id,
            number: lg.title,
            title: lg.title,
            description: lg.description || '',
            objectives: [lg.title] // For main LGs, we'll use the LG title as the "objective" for mapping
          });
          
          // Add sub learning goals if they exist
          if (lg.objectives && lg.objectives.length > 0) {
            lg.objectives.forEach(obj => {
              // Format sub learning goal with LG prefix
              const formattedSubLG = obj.sub_learning_goal_id.startsWith('LG') 
                ? obj.sub_learning_goal_id 
                : `LG${obj.sub_learning_goal_id}`;
                
              flatLearningGoals.push({
                id: `${lg.id}-${obj.sub_learning_goal_id}`,
                number: formattedSubLG,
                title: formattedSubLG,
                description: obj.description || '',
                objectives: [formattedSubLG]
              });
            });
          }
        });
        
        console.log("Setting allLearningGoals:", { 
          count: flatLearningGoals.length, 
          goals: flatLearningGoals.map(g => ({ id: g.id, number: g.number, objectivesCount: g.objectives.length }))
        });
        
        setAllLearningGoals(flatLearningGoals);
        // const formattedLGs = selectedTemplate.LG.map(lg => ({
        //     id: lg.id,
        //   number: lg.title.split(' ')[1],
        //     objectives: lg.objectives.map(obj => obj.sub_learning_goal_id)
        //   }));
        //   setAllLearningGoals(formattedLGs);
      } else {
        console.log("Template has no LG property:", selectedTemplate);
      }
    } else {
      console.log("No selectedTemplate available");
    }
  }, [selectedTemplate]);
  
  // Handle restoring data when returning from evidence page or report generation
  useEffect(() => {
    if (location.state?.returnedFromEvidence) {
      console.log("Restoring data from evidence page with complete state:", {
        hasOriginalData: !!location.state.originalData,
        hasFullAlignmentData: !!location.state.fullAlignmentData,
        hasOriginalTableData: !!location.state.originalTableData,
        isSingleCourseMode: location.state.isSingleCourseMode
      });
      
      // Set the active tab to Course Alignment Overview
      setActiveTab('Course Alignment Overview');
      
      // First, check if we have the full alignment data
      if (location.state.fullAlignmentData) {
        // Directly restore the full alignment data
        setAlignmentData(location.state.fullAlignmentData);
        //console.log("Restored complete alignment data");
      }
      // If not, but we have original course data, set up minimal alignment data
      else if (location.state.originalData) {
        const courseData = location.state.originalData;
        const courseName = location.state.courseName;
        
        if (!courseName) {
          console.error("Missing course name in navigation state");
          return;
        }
        
        //console.log(`Restoring data for course: ${courseName}`);
        
        // Create or update alignment data object
        setAlignmentData(prevData => {
          const newData = prevData ? {...prevData} : {};
          newData[courseName] = courseData;
          return newData;
        });
      }
      
      // If we have courses data, restore it
      if (location.state.allCoursesData) {
        setCoursesData(location.state.allCoursesData);
        //console.log("Restored courses data");
      }
      
      // If we have indirect scores data, restore it
      if (location.state.indirectScores) {
        setIndirectScores(location.state.indirectScores);
        //console.log("Restored indirect scores data");
      }
      
      // If we have survey data, restore it
      if (location.state.surveys) {
        setSurveys(location.state.surveys);
      }
      if (location.state.selectedSurvey) {
        setSelectedSurvey(location.state.selectedSurvey);
      }
      
      // Mark that we've returned from evidence to avoid unnecessary loading
      setIsReturnedFromEvidence(true);
      
      // Skip loading state
      setIsLoading(false);
    }
    
    // Handle returning from report generation
    if (returnedFromReportGeneration || location.state?.returnedFromReportGeneration) {
      console.log("Restoring data from report generation:", {
        hasSelectedTemplate: !!location.state.selectedTemplate,
        hasTemplateId: !!location.state.templateId,
        hasProgramData: !!location.state.programData,
        templateComplete: isTemplateComplete(location.state.selectedTemplate)
      });
      
      // Restore the selected template if available and complete
      if (location.state.selectedTemplate && isTemplateComplete(location.state.selectedTemplate)) {
        console.log("Restoring complete template from navigation state");
        setSelectedTemplate(location.state.selectedTemplate);
      } else if (location.state.templateId) {
        console.log("Template incomplete, trying to load complete version from localStorage");
        // Try to load complete template from localStorage
        const templateStorageKey = getTemplateStorageKey();
        const savedTemplate = localStorage.getItem(templateStorageKey);
        
        if (savedTemplate) {
          try {
            const parsedTemplate = JSON.parse(savedTemplate);
            if (isTemplateComplete(parsedTemplate)) {
              console.log("Found complete template in localStorage");
              setSelectedTemplate(parsedTemplate);
            } else {
              console.log("Template in localStorage is also incomplete, will need to fetch");
              setShouldFetchTemplate(true);
            }
          } catch (err) {
            console.error("Error parsing template from localStorage:", err);
            setShouldFetchTemplate(true);
          }
        } else {
          console.log("No saved template found, will need to fetch");
          setShouldFetchTemplate(true);
        }
      }
      // Restore courseList if present in navigation state
      if (location.state.courseList && location.state.courseList.length > 0) {
        // Only set if different from current courseList
        if (JSON.stringify(courseList) !== JSON.stringify(location.state.courseList)) {
          // Re-initialize coursesData from courseList
          const coursesToUse = isCourse ? [location.state.courseList[0]] : location.state.courseList;
          const totalObjectives = allLearningGoals.reduce((sum, lg) => sum + lg.objectives.length, 0);
          const newCoursesData = coursesToUse.map((courseCode, index) => ({
            id: index + 1,
            name: courseCode,
            mapping: Array(totalObjectives).fill().map(() => ({ checked: true })),
            score: Math.floor(Math.random() * 80) + 20,
            color: ['#AF5C4A', '#468171', '#E8A84B'][index % 3]
          }));
          setCoursesData(newCoursesData);
        }
      }
      // Set the active tab based on the navigation state
      if (location.state.tab) {
        setActiveTab(location.state.tab);
      }
      // Skip loading state since we're returning from report generation
      setIsLoading(false);
      
      // Don't trigger template fetching if we have a complete template
      if (location.state.selectedTemplate && isTemplateComplete(location.state.selectedTemplate)) {
        setIsReturnedFromEvidence(true);
      }
    }
  }, [location.state, allLearningGoals]);
  
  // Try to recover state from sessionStorage if we don't have state in location
  useEffect(() => {
    if (!location.state?.returnedFromEvidence && !location.state?.returnedFromReportGeneration && !isReturnedFromEvidence && !selectedTemplate) {
      try {
        // Check for evidence state first
        const savedEvidenceState = sessionStorage.getItem('return_from_evidence_state');
        if (savedEvidenceState) {
          const parsedState = JSON.parse(savedEvidenceState);
          //console.log("Recovered return state from sessionStorage");
          
          // Similar handling as above for the recovered state
          if (parsedState.fullAlignmentData) {
            setAlignmentData(parsedState.fullAlignmentData);
          } else if (parsedState.originalData) {
            const courseData = parsedState.originalData;
            const courseName = parsedState.courseName;
            
            if (courseName) {
              setAlignmentData(prevData => {
                const newData = prevData ? {...prevData} : {};
                newData[courseName] = courseData;
                return newData;
              });
            }
          }
          
          if (parsedState.allCoursesData) {
            setCoursesData(parsedState.allCoursesData);
          }
          
          if (parsedState.indirectScores) {
            setIndirectScores(parsedState.indirectScores);
          }
          
          if (parsedState.surveys) {
            setSurveys(parsedState.surveys);
          }
          if (parsedState.selectedSurvey) {
            setSelectedSurvey(parsedState.selectedSurvey);
          }
          
          // Set active tab
          setActiveTab('Course Alignment Overview');
          
          // Mark as returned
          setIsReturnedFromEvidence(true);
          
          // Skip loading
          setIsLoading(false);
          
          // Clear the sessionStorage to avoid reusing it inappropriately
          sessionStorage.removeItem('return_from_evidence_state');
          return;
        }
        
        // Check for report generation return state
        const savedReportState = sessionStorage.getItem('report_generation_return_state');
        if (savedReportState) {
          const parsedState = JSON.parse(savedReportState);
          console.log("Recovered report generation return state from sessionStorage");
          
          // Check if the state is recent (within last 5 minutes)
          const fiveMinutesAgo = Date.now() - (5 * 60 * 1000);
          if (parsedState.timestamp && parsedState.timestamp > fiveMinutesAgo) {
            // Restore the selected template if available and complete
            if (parsedState.selectedTemplate && isTemplateComplete(parsedState.selectedTemplate)) {
              console.log("Restoring complete template from sessionStorage");
              setSelectedTemplate(parsedState.selectedTemplate);
              
              // Set default tab
              setActiveTab('Curriculum Mapping');
              
              // Skip loading state
              setIsLoading(false);
              
              // Don't trigger template fetching
              setIsReturnedFromEvidence(true);
            } else {
              console.log("Template from sessionStorage is incomplete, will need to fetch");
              // Try to load complete template from localStorage as fallback
              const templateStorageKey = getTemplateStorageKey();
              const savedTemplate = localStorage.getItem(templateStorageKey);
              
              if (savedTemplate) {
                try {
                  const parsedTemplate = JSON.parse(savedTemplate);
                  if (isTemplateComplete(parsedTemplate)) {
                    console.log("Found complete template in localStorage as fallback");
                    setSelectedTemplate(parsedTemplate);
                    setActiveTab('Curriculum Mapping');
                    setIsLoading(false);
                    setIsReturnedFromEvidence(true);
                  } else {
                    console.log("Template in localStorage is also incomplete, will trigger fetch");
                    setShouldFetchTemplate(true);
                  }
                } catch (err) {
                  console.error("Error parsing template from localStorage:", err);
                  setShouldFetchTemplate(true);
                }
              } else {
                console.log("No saved template found, will trigger fetch");
                setShouldFetchTemplate(true);
              }
            }
          }
          
          // Clear the sessionStorage regardless of whether we used it
          sessionStorage.removeItem('report_generation_return_state');
        }
      } catch (err) {
        console.error("Failed to recover state from sessionStorage:", err);
      }
    }
  }, [location.state, isReturnedFromEvidence]);
  
  // Set initial tab based on URL parameter or navigation state
  useEffect(() => {
    // First check for tab in navigation state (higher priority)
    if (location.state?.tab) {
      const tabName = location.state.tab === 'course-alignment-overview' 
                    ? 'Course Alignment Overview' 
                    : location.state.tab;
      setActiveTab(tabName);
      return;
    }
    
    // Then check URL parameters
    const urlParams = new URLSearchParams(window.location.search);
    const tabParam = urlParams.get('tab');
    
    if (tabParam) {
      // Convert URL-friendly tab name back to the component's format
      const tabNameMap = {
        'curriculum-mapping': 'Curriculum Mapping',
        'course-alignment-overview': 'Course Alignment Overview',
        'indirect-method-summary': 'Indirect Method Summary',
        'recommendations': 'Recommendations',
        'micro-credentials': 'Micro-credentials',
        'career-assessment': 'Career Assessment',
        'progress-trends': 'Progress Trends'
      };
      
      const tabName = tabNameMap[tabParam];
      if (tabName) {
        setActiveTab(tabName);
      }
    }
  }, [location]);
  
  // Determine if we should load data when component mounts
  useEffect(() => {
    console.log("Initial mount check", {
      hasState: !!location.state,
      returnedFromEvidence: !!location.state?.returnedFromEvidence,
      returnedFromReportGeneration: !!location.state?.returnedFromReportGeneration,
      hasSelectedTemplate: !!selectedTemplate
    });
    
    // If returning from evidence page, don't load templates
    if (location.state?.returnedFromEvidence) {
      //console.log("Skipping template fetch - returning from evidence");
      setIsReturnedFromEvidence(true);
      // Set the active tab based on the state
      if (location.state.tab) {
        setActiveTab(location.state.tab === 'course-alignment-overview' 
                    ? 'Course Alignment Overview' 
                    : location.state.tab);
      }
    } 
    // If returning from report generation, don't load templates
    else if (returnedFromReportGeneration || location.state?.returnedFromReportGeneration) {
      //console.log("Skipping template fetch - returning from report generation");
      setIsReturnedFromEvidence(true);
      // Set the active tab based on the state
      if (location.state.tab) {
        setActiveTab(location.state.tab);
      }
    }
    // If we already have a selected template, don't need to load
    else if (selectedTemplate) {
      //console.log("Already have selected template, skipping fetch");
    }
    // Otherwise, we should fetch template
    else {
      //console.log("Should fetch template on mount");
      setShouldFetchTemplate(true);
    }
  }, []);
  
  // Fetch surveys on mount or when school changes
  useEffect(() => {
    if (!isReturnedFromEvidence) {
      fetchSurveys();
    }
  }, [school, isReturnedFromEvidence]);
  
  // Fetch templates only when needed
  useEffect(() => {
    const fetchTemplate = async () => {
      console.log("fetchTemplate called:", {
        shouldFetchTemplate,
        isReturnedFromEvidence,
        returnedFromReportGeneration,
        hasLocationState: !!location.state?.returnedFromReportGeneration,
        hasSelectedTemplate: !!selectedTemplate
      });
      
      if (!shouldFetchTemplate || isReturnedFromEvidence || returnedFromReportGeneration || location.state?.returnedFromReportGeneration || selectedTemplate) {
        console.log("Skipping template fetch - conditions not met");
        return;
      }
      
      console.log("Fetching template data");
      setIsLoading(true);
      try {
        // Get school parameter from URL
        const urlParams = new URLSearchParams(window.location.search);
        const schoolParam = urlParams.get('school') || school;
        
        // Check if there's a saved template in localStorage
        const templateStorageKey = getTemplateStorageKey();
        const savedTemplate = localStorage.getItem(templateStorageKey);
        
        if (savedTemplate) {
          try {
            const parsedTemplate = JSON.parse(savedTemplate);
            setSelectedTemplate(parsedTemplate);
            //console.log("Loaded template from localStorage:", parsedTemplate.name);
            setIsLoading(false);
            setShouldFetchTemplate(false);
            return;
          } catch (err) {
            console.error("Error parsing saved template:", err);
            // Continue with fetching templates if parsing fails
          }
        }
        
        // Fetch all templates but don't auto-select - let the modal handle selection
        const data = await getAllTemplates(schoolParam);
        
        if (data.templates && data.templates.length > 0) {
          // Don't auto-select template - show modal instead
          //console.log("Templates fetched, showing selection modal");
          setShowLearningGoalsModal(true);
        } else {
          //console.log("No templates found for this school");
          setError("No templates found. Please create a template first.");
        }
      } catch (err) {
        console.error("Error fetching templates:", err);
        setError("Failed to load templates. Please try again later.");
      } finally {
        setIsLoading(false);
        setShouldFetchTemplate(false);
      }
    };
    
    fetchTemplate();
  }, [shouldFetchTemplate, isReturnedFromEvidence, school]);
  
  // Initialize coursesData once allLearningGoals are loaded
  useEffect(() => {
    console.log("coursesData useEffect triggered:", {
      allLearningGoalsLength: allLearningGoals.length,
      courseListLength: courseList?.length || 0,
      isReturnedFromEvidence,
      existingCoursesCount: coursesData.length
    });
    
    // Skip if returning from evidence page with existing coursesData
    if (isReturnedFromEvidence && coursesData.length > 0) {
      console.log("Using existing coursesData from evidence page return");
      return;
    }
    
    if (allLearningGoals.length === 0) {
      console.log("Skipping coursesData initialization - no learning goals");
      return;
    }
    
    console.log("Initializing coursesData", {
      courseList,
      isCourse,
      allLearningGoalsCount: allLearningGoals.length,
      isReturnedFromEvidence,
      existingCoursesCount: coursesData.length
    });
    
    // Calculate total objectives for the mapping array size
    const totalObjectives = allLearningGoals.reduce((sum, lg) => sum + lg.objectives.length, 0);
    
    // Check if there are saved selections in local storage
    const storageKey = getStorageKey();
    const savedSelections = localStorage.getItem(storageKey);
    
    if (courseList && courseList.length > 0) {
      // Filter courses if in course mode (showing just one course)
      // or pass all courses if in program mode (showing all courses)
      const coursesToUse = isCourse ? [courseList[0]] : courseList;
      
      //console.log(`Mode: ${isCourse ? 'Course Assessment' : 'Program Assessment'}, Showing ${coursesToUse.length} course(s): ${coursesToUse.join(', ')}`);
      
      // Map the course codes from coursesToUse to course objects
      const newCoursesData = coursesToUse.map((courseCode, index) => {
        // If we have saved selections, use them
        if (savedSelections) {
          const parsedSelections = JSON.parse(savedSelections);
          const courseSelections = parsedSelections.find(c => c.name === courseCode);
          
          if (courseSelections) {
            // Make sure score is a number
            const score = typeof courseSelections.score === 'number' ? 
              courseSelections.score : Math.floor(Math.random() * 80) + 20;
            
            console.log('Loading course from localStorage:', courseCode, courseSelections);
            
            return {
              ...courseSelections,
              id: courseCode, // Ensure ID is the course name for consistency
              score: score
            };
          }
        }
        
        // Otherwise, create with all checkboxes checked by default
        return {
          id: courseCode, // Use course name as ID for consistency
          name: courseCode,
          mapping: Array(totalObjectives).fill().map(() => ({ checked: true })),
          score: Math.floor(Math.random() * 80) + 20,
          color: ['#AF5C4A', '#468171', '#E8A84B'][Math.floor(Math.random() * 3)]
        };
      });
      
      console.log('newCoursesData created:', newCoursesData);
      setCoursesData(newCoursesData);
    } else {
      // Default courses if no courseList provided - only set if coursesData is currently empty
      // This prevents infinite loops when returning from ReportGeneration with empty courseList
      if (coursesData.length === 0 && !isReturnedFromEvidence) {
        setCoursesData([]);
      }
    }
  }, [allLearningGoals, courseList, isCourse, isReturnedFromEvidence]);
  
  // Save selections to local storage when they change
  useEffect(() => {
    if (coursesData.length > 0 && !isLoading) {
      const storageKey = getStorageKey();
      localStorage.setItem(storageKey, JSON.stringify(coursesData));
    }
  }, [coursesData, isLoading]);
  
  // Cleanup effect for component unmount
  useEffect(() => {
    return () => {
      if (fetchTimeoutRef.current) {
        clearTimeout(fetchTimeoutRef.current);
      }
    };
  }, []);
  
  // Close export dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (exportRef.current && !exportRef.current.contains(event.target)) {
        setShowExportDropdown(false);
      }
      if (fileTypeRef.current && !fileTypeRef.current.contains(event.target)) {
        setShowFileTypeDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);
  
  // Number of LGs to display at once - now display all of them
  // const visibleLgsCount = 5;
  
  // All learning goals are now visible
  const visibleLearningGoals = allLearningGoals;
  
  // Calculate total objectives for mapping array
  const totalObjectives = visibleLearningGoals.reduce((sum, lg) => sum + lg.objectives.length, 0);
  
  const years = [
    '2025 Spring',
    '2024 Fall',
    '2024 Spring',
    '2023 Fall'
  ];

  const handleCheckboxChange = (courseIndex, mappingIndex) => {
    if (!editMode) return;
    
    const newCoursesData = [...coursesData];
    const currentMapping = newCoursesData[courseIndex].mapping[mappingIndex];
    
    // Toggle checked status
    currentMapping.checked = !currentMapping.checked;
    
    setCoursesData(newCoursesData);
  };

  const handleYearClick = () => {
    setShowYearDropdown(!showYearDropdown);
  };

  const selectYear = (year) => {
    setAcademicYear(year);
    setShowYearDropdown(false);
  };
  
  const handleExportClick = () => {
    setShowExportDropdown(!showExportDropdown);
  };

  const handleReportGenerationClick = () => {
    // Get school from URL parameters
    const urlParams = new URLSearchParams(window.location.search);
    const school = urlParams.get('school') || 'MARYLAND';
    
    // Store template_id in localStorage for ReportGeneration to use
    if (selectedTemplate?.id) {
      localStorage.setItem('report_generation_template_id', selectedTemplate.id.toString());
      console.log('AssuranceReport - Stored template_id for ReportGeneration:', selectedTemplate.id);
    } else {
      console.warn('AssuranceReport - No selectedTemplate.id available for ReportGeneration');
    }
    
    // Pass courseList in navigation state
    navigate(`/report-generation?school=${school}`, {
      state: {
        programName: displayProgramName,
        programData: { name: displayProgramName, category: 'Undergraduate', description: '' },
        courseList: courseList, // <-- pass courseList here
        returnTo: `/assessment?school=${school}`,
        ...location.state
      }
    });
  };
  
  const handleExportOption = async () => {
    try {
      if (fileType !== 'Excel') {
        addNotification('Currently only Excel export is supported', 'warning');
    setShowExportDropdown(false);
        return;
      }

      // Show export loading state, but keep UI visible underneath
      const overlayDiv = document.createElement('div');
      overlayDiv.className = 'export-overlay';
      
      const loadingContainer = document.createElement('div');
      loadingContainer.className = 'export-loading-container';
      
      const loadingIndicator = document.createElement('div');
      loadingIndicator.className = 'loading-indicator';
      
      const loadingText = document.createElement('p');
      loadingText.textContent = 'Exporting Excel report...';
      
      loadingContainer.appendChild(loadingIndicator);
      loadingContainer.appendChild(loadingText);
      overlayDiv.appendChild(loadingContainer);
      
      document.querySelector('.assurance-report').appendChild(overlayDiv);
      
      // Close the dropdown
      setShowExportDropdown(false);

      // Build details object for API request
      const details = {};
      
      // For each course, find which learning objectives are checked
      coursesData.forEach(course => {
        const selectedLOs = [];
        let currentIndex = 0;
        
        // Process all learning goals to find checked objectives
        for (let i = 0; i < allLearningGoals.length; i++) {
          const lg = allLearningGoals[i];
          for (let j = 0; j < lg.objectives.length; j++) {
            if (course.mapping[currentIndex]?.checked) {
              // Format the objective value based on its format
              let objectiveValue;
              const objective = lg.objectives[j];
              
              // If it starts with "LG", extract the numeric part
              if (typeof objective === 'string' && objective.startsWith('LG')) {
                objectiveValue = objective.replace(/\D/g, '');
              } else {
                // Otherwise use as is (likely already a number or formatted correctly)
                objectiveValue = parseFloat(objective) || objective;
              }
              
              // Format the learning goal ID properly (e.g., convert 11 to 1.1)
              objectiveValue = formatLearningGoalId(objectiveValue);
              
              selectedLOs.push(objectiveValue);
            }
            currentIndex++;
          }
        }
        
        // Only add courses with selected learning objectives
        if (selectedLOs.length > 0) {
          details[course.name] = selectedLOs;
        }
      });
      
      // Only make API call if there are courses with selected learning objectives
      if (Object.keys(details).length > 0 && selectedTemplate) {
        // Use the selected template ID for the API call
        const response = await exportExcelReport(selectedTemplate.id || 1, details);
        
        // Handle the new response format which returns a single download link
        if (response && response.download_link) {
          // Ensure download_link is a string before using it
          const downloadUrl = typeof response.download_link === 'string' 
            ? response.download_link 
            : String(response.download_link);
          
          // Open the download link in a new tab
          window.open(downloadUrl, '_blank');
        } else {
          throw new Error('Invalid response format from export API');
        }
      } else {
        addNotification('Please select at least one learning objective for any course', 'warning');
      }
    } catch (error) {
      console.error('Error exporting report:', error);
      addNotification('Failed to export report. Please try again later.', 'error');
    } finally {
      // Remove the export overlay
      const overlay = document.querySelector('.export-overlay');
      if (overlay) {
        overlay.remove();
      }
    }
  };
  
  const handleFileTypeClick = (e) => {
    e.stopPropagation();
    setShowFileTypeDropdown(!showFileTypeDropdown);
  };
  
  const selectFileType = (type) => {
    setFileType(type);
    setShowFileTypeDropdown(false);
  };
  
  const handleExportSelectionChange = (selection) => {
    if (selection === 'all') {
      // If "All" is clicked, handle appropriately
      const newValue = !exportSelections.all;
      setExportSelections({
        all: newValue,
        curriculumMapping: newValue ? false : exportSelections.curriculumMapping,
        courseAlignment: newValue ? false : exportSelections.courseAlignment,
        recommendation: newValue ? false : exportSelections.recommendation
      });
    } else {
      // For other selections, toggle their value and update "All" accordingly
      const newSelections = {
        ...exportSelections,
        [selection]: !exportSelections[selection],
        all: false // Uncheck "All" when a specific option is selected
      };
      setExportSelections(newSelections);
    }
  };

  // Get the correct mapping indices for the current visible LGs
  const getVisibleMappingIndices = () => {
    const indices = [];
    let currentIndex = 0;
    
    for (let i = 0; i < allLearningGoals.length; i++) {
      const lg = allLearningGoals[i];
      for (let j = 0; j < lg.objectives.length; j++) {
        indices.push(currentIndex + j);
      }
      currentIndex += lg.objectives.length;
    }
    
    console.log("getVisibleMappingIndices result:", {
      allLearningGoalsLength: allLearningGoals.length,
      indices: indices,
      currentIndex: currentIndex
    });
    
    return indices;
  };
  
  const visibleMappingIndices = getVisibleMappingIndices();

  // Get a map of courses to their selected learning goals
  const getSelectedLearningGoalsMap = () => {
    const courseToLGMap = {};
    
    coursesData.forEach((course) => {
      const selectedLGs = [];
      let currentIndex = 0;
      
      // Process all learning goals (not just visible ones)
      for (let i = 0; i < allLearningGoals.length; i++) {
        const lg = allLearningGoals[i];
        for (let j = 0; j < lg.objectives.length; j++) {
          if (course.mapping[currentIndex]?.checked) {
            selectedLGs.push(lg.objectives[j]); // Use objective directly which already has LG format
          }
          currentIndex++;
        }
      }
      
      courseToLGMap[course.id] = selectedLGs;
    });
    
    return courseToLGMap;
  };
  
  console.log('About to create courseToLGMap, coursesData:', coursesData);
  const courseToLGMap = getSelectedLearningGoalsMap();
  console.log('courseToLGMap created:', courseToLGMap);
  
  // Memoize tab content to prevent unnecessary rerenders
  const courseAlignmentOverviewContent = useMemo(() => (
    <CourseAlignmentOverview 
      coursesData={coursesData} 
      visibleLearningGoals={visibleLearningGoals}
      courseToLGMap={courseToLGMap}
      alignmentData={alignmentData}
      indirectScores={indirectScores}
      isLoading={alignmentLoading}
      surveys={surveys}
      selectedSurvey={selectedSurvey}
    />
  ), [coursesData, visibleLearningGoals, courseToLGMap, alignmentData, indirectScores, alignmentLoading, surveys, selectedSurvey]);
  
  // Also memoize the Recommendations component
  const recommendationsContent = useMemo(() => (
    <Recommendations 
      key="recommendations"
      courseToLGMap={courseToLGMap}
      allLearningGoals={allLearningGoals}
      coursesData={coursesData}
      programName={displayProgramName}
      isCourse={isCourse}
    />
  ), [courseToLGMap, allLearningGoals, coursesData, displayProgramName, isCourse]);
  
  // Log render count to diagnose re-renders
  const renderCountRef = useRef(0);
  useEffect(() => {
    renderCountRef.current += 1;
    //console.log(`AssuranceReport re-render #${renderCountRef.current}, activeTab: ${activeTab}`);
  }, [activeTab]);
  
  // Check if the template has sub learning goals
  
  
  // Render the appropriate content based on the active tab
  const renderTabContent = () => {
    console.log("Rendering tab content for", activeTab, {
      hasCoursesData: coursesData.length > 0,
      hasLearningGoals: allLearningGoals.length > 0,
      coursesDataLength: coursesData.length,
      allLearningGoalsLength: allLearningGoals.length
    });
    
    switch (activeTab) {
      case 'Course Alignment Overview':
        return courseAlignmentOverviewContent;
      case 'Curriculum Mapping':
        return (
          <>
            <div className="academic-year-selector">
              <label>Academic Year:</label>
              <div className="selector-dropdown-container">
                <div className="selector-dropdown" onClick={handleYearClick}>
                  <span>{academicYear}</span>
                  <span className="dropdown-arrow">▼</span>
                </div>
                {showYearDropdown && (
                  <div className="year-dropdown">
                    {years.map(year => (
                      <div 
                        key={year} 
                        className="year-option" 
                        onClick={() => selectYear(year)}
                      >
                        {year}
                      </div>
                    ))}
                  </div>
                )}
              </div>
              
              <div className="edit-mode-toggle">
                <span className="toggle-label">Edit Mode</span>
                <label className="switch">
                  <input 
                    type="checkbox" 
                    checked={editMode}
                    onChange={() => setEditMode(!editMode)}
                  />
                  <span className="slider round"></span>
                </label>
              </div>
            </div>
            <div className="curriculum-table-container">
              <div className="curriculum-table">
                <div className="table-scroll-note">
                  {allLearningGoals.length > 8 && "Scroll horizontally to view all learning goals →"}
                </div>
                <table>
                  <thead>
                    <tr>
                      <th className="first-col">Learning Goals</th>
                      {visibleLearningGoals.map(lg => (
                        <th key={lg.id} colSpan={lg.objectives.length}>{lg.number}</th>
                      ))}
                    </tr>
                    
                  </thead>
                  <tbody>
                    {coursesData.map((course, courseIndex) => {
                      console.log(`Rendering course ${course.name}:`, {
                        courseIndex,
                        mappingLength: course.mapping?.length || 0,
                        mappingData: course.mapping?.slice(0, 3) // Show first 3 items
                      });
                      
                      return (
                        <tr key={course.id}>
                          <td className="first-col">{course.name}</td>
                          {visibleMappingIndices.map((mappingIndex, i) => {
                            console.log(`Rendering checkbox for course ${course.name}, mappingIndex ${mappingIndex}:`, {
                              hasMapping: !!course.mapping[mappingIndex],
                              checked: course.mapping[mappingIndex]?.checked,
                              editMode
                            });
                            
                            return (
                              <td key={i} className={editMode ? 'editable' : ''}>
                                <div className="checkbox-container">
                                  <input 
                                    type="checkbox" 
                                    checked={course.mapping[mappingIndex]?.checked || false}
                                    onChange={() => handleCheckboxChange(courseIndex, mappingIndex)}
                                    className="mapping-checkbox"
                                    disabled={!editMode}
                                  />
                                </div>
                              </td>
                            );
                          })}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
            
            {/* Survey Selection Section */}
            <div className="survey-selection-main-section">
              <div className="survey-section-header">
                <div className="survey-section-title">
                  <h2>Survey Selection for Indirect Method</h2>
                  <p className="survey-section-description">
                    Choose a survey to calculate indirect method scores, or leave unselected to show N/A.
                  </p>
                </div>
                {selectedSurvey && (
                  <div className="selected-survey-summary">
                    <div className="summary-card">
                      <div className="summary-header">Currently Selected</div>
                      <div className="summary-content">
                        <div className="summary-filename">{selectedSurvey.original_filename}</div>
                        <div className="summary-details">
                          <span className="summary-program">{selectedSurvey.program}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
              
              {surveysLoading ? (
                <div className="surveys-loading-state">
                  <div className="loading-spinner"></div>
                  <p>Loading available surveys...</p>
                </div>
              ) : surveys.length === 0 ? (
                <div className="no-surveys-state">
                  <div className="empty-state-icon">📊</div>
                  <h3>No Surveys Available</h3>
                  <p>No surveys have been uploaded for this school yet.</p>
                </div>
              ) : (
                <div className="surveys-grid-container">
                  <div className="survey-card no-selection-card" onClick={() => setSelectedSurvey(null)}>
                    <div className="survey-card-header">
                      <div className="survey-radio">
                        <input
                          type="radio"
                          name="survey-selection"
                          checked={!selectedSurvey}
                          onChange={() => setSelectedSurvey(null)}
                        />
                      </div>
                      <div className="survey-status no-selection">
                        <span className="status-indicator">○</span>
                        None
                      </div>
                    </div>
                    <div className="survey-card-content">
                      <h3 className="survey-title">No Survey Selected</h3>
                      <p className="survey-description">
                        Indirect method scores will show as N/A
                      </p>
                    </div>
                  </div>
                  
                  {surveys.map(survey => {
                    const isSelected = selectedSurvey?.id === survey.id;
                    
                    return (
                      <div 
                        key={survey.id} 
                        className={`survey-card ${isSelected ? 'selected' : ''}`}
                        onClick={() => setSelectedSurvey(survey)}
                      >
                        <div className="survey-card-header">
                          <div className="survey-radio">
                            <input
                              type="radio"
                              name="survey-selection"
                              checked={isSelected}
                              onChange={() => setSelectedSurvey(survey)}
                            />
                          </div>
                        </div>
                        
                        <div className="survey-card-content">
                          <h3 className="survey-title" title={survey.original_filename}>
                            {survey.original_filename}
                          </h3>
                          
                          <div className="survey-program">
                            <span className="program-label">Program:</span>
                            <span className="program-value">{survey.program}</span>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </>
        );
      case 'Indirect Method Summary':
        return <IndirectMethodSummary 
          programName={displayProgramName}
          school={school}
        />;
      case 'Recommendations':
        //console.log("Returning memoized recommendations");
        return recommendationsContent;
      case 'Micro-credentials':
        return isCourse ? (
          <MicroCredentials 
            school={school} 
            courseName={displayProgramName} 
          />
        ) : (
          <MicroCredentials 
            school={school} 
            programName={displayProgramName} 
          />
        );
      case 'Career Assessment':
        return (
          <div className="career-assessment-container">
            <div className="career-assessment-layout">
              {/* Left Side - Job Prospects */}
              <div className="job-prospects-section">
                <div className="job-prospects-header">
                  <h2>Job Prospects</h2>
                  <div className="level-dropdown">
                    <select className="level-select">
                      <option value="all">All Levels</option>
                      <option value="entry">Entry Level</option>
                      <option value="mid">Mid Level</option>
                      <option value="senior">Senior Level</option>
                    </select>
                  </div>
                </div>
                
                <div className="job-prospects-list">
                  {careerLoading ? (
                    <div className="loading-message">Loading career data...</div>
                  ) : careerError ? (
                    <div className="error-message">{careerError}</div>
                  ) : careerData.length === 0 ? (
                    <div className="no-data-message">No career data available</div>
                  ) : (
                    careerData.slice((currentPage - 1) * jobsPerPage, currentPage * jobsPerPage).map((job, index) => {
                      const matchPercentage = calculateMatchPercentage(job.required_skills, job.missing_skills);
                      const circumference = 2 * Math.PI * 33.5; // radius = 33.5
                      const strokeDashoffset = circumference - (matchPercentage / 100) * circumference;
                      const isSelected = selectedJob && selectedJob.id === job.id;
                      
                      return (
                        <div 
                          key={job.id} 
                          className={`job-card ${isSelected ? 'selected' : ''}`}
                          onClick={() => handleJobSelect(job)}
                        >
                          <div className="job-card-content">
                            <h3>{job.title}</h3>
                            <div className="skills-info">
                              <span className="skills-count">{job.required_skills.length}</span>
                              <span className="skills-label">Skills Needed</span>
                            </div>
                            <p className="job-stats">
                              Job Growth: {formatJobGrowth(job.job_growth_pct)}<br />
                              Avg. Salary: {formatSalary(job.avg_salary)}
                            </p>
                          </div>
                          <div className="match-chart">
                            <div className="circular-progress">
                              <svg className="progress-ring" width="84" height="84">
                                <circle
                                  className="progress-ring-circle-bg"
                                  stroke="#F1F1F1"
                                  strokeWidth="12"
                                  fill="transparent"
                                  r="33.5"
                                  cx="42"
                                  cy="42"
                                />
                                <circle
                                  className="progress-ring-circle"
                                  stroke="#2E2FB9"
                                  strokeWidth="12"
                                  fill="transparent"
                                  r="33.5"
                                  cx="42"
                                  cy="42"
                                  strokeDasharray={circumference}
                                  strokeDashoffset={strokeDashoffset}
                                  strokeLinecap="round"
                                />
                              </svg>
                              <span className="match-percentage">
                                <span>{matchPercentage}%</span>
                                <span>match</span>
                              </span>
                            </div>
                          </div>
                        </div>
                      );
                    })
                  )}
                </div>
                
                <div className="pagination">
                  <button 
                    className="page-btn prev"
                    onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                    disabled={currentPage === 1}
                  >
                    <svg width="8" height="14" viewBox="0 0 12 24" fill="none">
                      <path d="M9 3L3 12L9 21" stroke="#BBBBBB" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </button>
                  <span className="page-info">
                    Page {currentPage} of {Math.ceil(careerData.length / jobsPerPage)}
                  </span>
                  <button 
                    className="page-btn next"
                    onClick={() => setCurrentPage(prev => Math.min(Math.ceil(careerData.length / jobsPerPage), prev + 1))}
                    disabled={currentPage >= Math.ceil(careerData.length / jobsPerPage)}
                  >
                    <svg width="8" height="14" viewBox="0 0 12 24" fill="none">
                      <path d="M3 3L9 12L3 21" stroke="#3B3B3B" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </button>
                </div>
              </div>
              
              {/* Right Side - Job Details */}
              <div className="job-details-section">
                <div className="job-details-container">
                  <div className="job-details-content">
                    {selectedJob ? (
                      <>
                        {/* Job Header */}
                        <div className="job-details-header">
                          <h2>{selectedJob.title}</h2>
                          <div className="job-match-badge">
                            {calculateMatchPercentage(selectedJob.required_skills, selectedJob.missing_skills)}% Match
                          </div>
                        </div>
                        
                        {/* Summary Cards */}
                        <div className="summary-cards">
                          <div className="summary-card">
                            <div className="summary-value">{formatSalary(selectedJob.avg_salary)}</div>
                            <div className="summary-label">Average Salary</div>
                          </div>
                          <div className="summary-card">
                            <div className="summary-value">{formatJobGrowth(selectedJob.job_growth_pct)}</div>
                            <div className="summary-label">Job Growth</div>
                          </div>
                          <div className="summary-card">
                            <div className="summary-value">{selectedJob.required_skills.length}</div>
                            <div className="summary-label">Skills Needed</div>
                          </div>
                        </div>
                      </>
                    ) : (
                      <div className="no-job-selected">
                        <p>Select a job to view details</p>
                      </div>
                    )}
                    
                    {/* Skill Gap Analysis */}
                    {selectedJob && (
                      <div className="skill-gap-section">
                        <div className="skill-gap-header">
                          <div className="skill-gap-title">
                            <img src="/plant.svg" alt="Plant" className="skill-gap-icon" />
                            <h3>Skill Gap Analysis</h3>
                          </div>
                          <button className="skill-gap-collapse-btn">
                            <svg width="14" height="8" viewBox="0 0 24 12" fill="none">
                              <path d="M3 9L12 3L21 9" stroke="#525252" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                            </svg>
                          </button>
                        </div>
                        
                        <div className="skills-table">
                          <div className="skills-header">
                            <div className="skills-column">
                              <h4>Current Skills ({selectedJob.required_skills.length - selectedJob.missing_skills.length})</h4>
                            </div>
                            <div className="skills-column">
                              <h4>Skills to Develop ({selectedJob.missing_skills.length})</h4>
                            </div>
                          </div>
                          
                          <div className="skills-content">
                            <div className="skills-column">
                              <div className="skills-tags" ref={currentSkillsRef}>
                                {getSkillsToDisplay(
                                  selectedJob.required_skills.filter(skill => !selectedJob.missing_skills.includes(skill)),
                                  true
                                ).map((skill, index) => (
                                  <span key={index} className="skill-tag">{skill}</span>
                                ))}
                                {getRemainingSkillsCount(
                                  selectedJob.required_skills.filter(skill => !selectedJob.missing_skills.includes(skill)),
                                  true
                                ) > 0 && (
                                  <span 
                                    className="skill-tag more-skills" 
                                    onClick={handleCurrentSkillsToggle}
                                  >
                                    + {getRemainingSkillsCount(
                                      selectedJob.required_skills.filter(skill => !selectedJob.missing_skills.includes(skill)),
                                      true
                                    )} more skills
                                  </span>
                                )}
                                {currentSkillsExpanded && (
                                  <span 
                                    className="skill-tag collapse-skills" 
                                    onClick={handleCurrentSkillsToggle}
                                  >
                                    Collapse skills
                                  </span>
                                )}
                              </div>
                            </div>
                            <div className="skills-column">
                              <div className="skills-tags" ref={missingSkillsRef}>
                                {getSkillsToDisplay(selectedJob.missing_skills, false).map((skill, index) => (
                                  <span key={index} className="skill-tag">{skill}</span>
                                ))}
                                {getRemainingSkillsCount(selectedJob.missing_skills, false) > 0 && (
                                  <span 
                                    className="skill-tag more-skills" 
                                    onClick={handleMissingSkillsToggle}
                                  >
                                    + {getRemainingSkillsCount(selectedJob.missing_skills, false)} more skills
                                  </span>
                                )}
                                {missingSkillsExpanded && (
                                  <span 
                                    className="skill-tag collapse-skills" 
                                    onClick={handleMissingSkillsToggle}
                                  >
                                    Collapse skills
                                  </span>
                                )}
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                    
                    {/* Recommended Courses */}
                    {selectedJob && (
                      <div className="recommended-courses-section">
                        <div className="recommended-courses-header">
                          <div className="recommended-courses-title">
                            <img src="/book-open.svg" alt="Book Open" className="recommended-courses-icon" />
                            <h3>Recommended New Courses</h3>
                          </div>
                          <button className="recommended-courses-collapse-btn">
                            <svg width="14" height="8" viewBox="0 0 24 12" fill="none">
                              <path d="M3 9L12 3L21 9" stroke="#525252" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                            </svg>
                          </button>
                        </div>
                        
                        <div className="courses-list">
                          {selectedJob?.recommended_courses?.map((course, index) => (
                            <div key={course.id || index} className="course-item">
                              <div className="course-progress">
                                <span className="progress-text">{index + 1}/{selectedJob.recommended_courses.length}</span>
                              </div>
                              <div className="course-title">
                                {course.course_title}
                              </div>
                              <button 
                                className="view-syllabus-btn"
                                onClick={(e) => handleViewSyllabus(course.syllabus_url, course.course_title, e)}
                              >
                                View Syllabus
                              </button>
                            </div>
                          )) || (
                            <div className="no-courses-message">
                              <p>No recommended courses available for this position.</p>
                            </div>
                          )}
                        </div>
                        
                        <div className="courses-pagination">
                          <button className="page-btn prev" disabled>
                            <svg width="8" height="14" viewBox="0 0 12 24" fill="none">
                              <path d="M9 3L3 12L9 21" stroke="#BBBBBB" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                            </svg>
                          </button>
                          <span className="page-info">Page 1 of 1</span>
                          <button className="page-btn next" disabled>
                            <svg width="8" height="14" viewBox="0 0 12 24" fill="none">
                              <path d="M3 3L9 12L3 21" stroke="#BBBBBB" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                            </svg>
                          </button>
                        </div>
                      </div>
                    )}
                    
                    {/* Action Bar - at the bottom of job-details-content */}
                    {selectedJob && (
                      <div className="action-bar">
                        <div className="boost-message">
                          Adding these courses boost job readiness by <span className="boost-percentage">+{boostPercentage}%</span>
                        </div>
                        <button className="configure-btn" onClick={() => setShowCourseManagement(true)}>
                          Configure Courses
                          <svg width="8" height="16" viewBox="0 0 12 24" fill="none">
                            <path d="M3 3L9 12L3 21" stroke="#FFFFFF" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                          </svg>
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        );
      case 'Progress Trends':
        return (
          <div className="progress-trends-container">
            <div className="progress-trends-header">
              <div className="lg-header">
                <h2>LG1</h2>
                <div className="lg-icon">
                  <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <rect width="16" height="16" x="2" y="2" rx="2" fill="#E5E7EB"/>
                    <path d="M8 6h4M8 10h4M8 14h2" stroke="#6B7280" strokeWidth="1.5" strokeLinecap="round"/>
                    <circle cx="12" cy="8" r="1" fill="#6B7280"/>
                  </svg>
                </div>
              </div>
              <p className="lg-description">
                Students shall demonstrate mastery of financial tools and apply them appropriately and innovatively to both identify and solve business problems.
              </p>
            </div>
            
            <div className="progress-chart-container">
              <div className="chart-wrapper">
                <div className="chart-y-axis">
                  <div className="y-label">100%</div>
                  <div className="y-label">80%</div>
                  <div className="y-label">60%</div>
                  <div className="y-label">40%</div>
                  <div className="y-label">20%</div>
                  <div className="y-label">0%</div>
                </div>
                
                <div className="chart-area">
                  <div className="chart-grid">
                    <div className="grid-line"></div>
                    <div className="grid-line"></div>
                    <div className="grid-line"></div>
                    <div className="grid-line"></div>
                    <div className="grid-line"></div>
                  </div>
                  
                  <div className="chart-data">
                    <svg className="chart-lines" width="100%" height="100%" viewBox="0 0 100 200" style={{position: 'absolute', top: 0, left: 0}}>
                      {/* Line connecting all data points */}
                      <polyline
                        points="10,58 27.5,45 45,43 62.5,20 80,28"
                        fill="none"
                        stroke="#2E2FB9"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                    <div className="data-line lg1-line">
                      <div className="data-point" style={{left: '10%', top: '58%'}}>
                        <div className="point-circle"></div>
                        <span className="point-value">42%</span>
                      </div>
                      <div className="data-point" style={{left: '27.5%', top: '45%'}}>
                        <div className="point-circle"></div>
                        <span className="point-value">55%</span>
                      </div>
                      <div className="data-point" style={{left: '45%', top: '43%'}}>
                        <div className="point-circle"></div>
                        <span className="point-value">57%</span>
                      </div>
                      <div className="data-point" style={{left: '62.5%', top: '20%'}}>
                        <div className="point-circle"></div>
                        <span className="point-value">80%</span>
                      </div>
                      <div className="data-point current" style={{left: '80%', top: '28%'}}>
                        <div className="point-circle filled"></div>
                        <span className="point-value">72%</span>
                      </div>
                    </div>
                    
                    <div className="average-line">
                      <div className="avg-line"></div>
                    </div>
                  </div>
                  
                  <div className="chart-x-axis">
                    <div className="x-label">F '22</div>
                    <div className="x-label">S '23</div>
                    <div className="x-label">F '23</div>
                    <div className="x-label">S '24</div>
                    <div className="x-label current-period">
                      F '24
                      <div className="current-label">(Current Selected Year)</div>
                    </div>
                  </div>
                </div>
              </div>
              
              <div className="chart-legend">
                <div className="legend-item">
                  <div className="legend-line lg1-line"></div>
                  <span>LG1 Alignment Score Over Years</span>
                </div>
                <div className="legend-item">
                  <div className="legend-line avg-line"></div>
                  <span>LG1 Avg. Alignment Score</span>
                </div>
              </div>
            </div>
          </div>
        );
      default:
        return null;
    }
  };

  // Fix export dropdown positioning and rendering
  const ExportDropdown = React.memo(({ 
    isVisible, 
    fileType, 
    exportSelections,
    showFileTypeDropdown,
    onCancel, 
    onExport, 
    onFileTypeClick,
    onFileTypeSelect,
    onSelectionChange,
    years
  }) => {
    if (!isVisible) return null;

  return (
              <div className="export-dropdown">
                <div className="export-header">
                  <h3>Export Options</h3>
                </div>
                <div className="export-content">
                  <div className="export-file-type">
                    <div className="export-label">File Type:</div>
                    <div className="file-type-selector-container">
                      <div className="file-type-selector" onClick={onFileTypeClick}>
                        <span>{fileType}</span>
                        <span className="dropdown-arrow">▼</span>
                      </div>
                      {showFileTypeDropdown && (
                        <div className="file-type-dropdown">
                          <div 
                            className="file-type-option"
                            onClick={() => onFileTypeSelect('Excel')}
                          >
                            Excel
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                  
                  <div className="export-actions">
                    <button 
                      className="export-action-button export-cancel"
                      onClick={onCancel}
                    >
                      Cancel
                    </button>
                    <button 
                      className="export-action-button export-confirm"
                      onClick={onExport}
                    >
                      Export
                    </button>
                  </div>
                </div>
              </div>
    );
  });

  // SelectLearningGoalsModal component
  const SelectLearningGoalsModal = ({ onClose, template, onSelectTemplate }) => {
    const [expandedSubLGs, setExpandedSubLGs] = useState({});
    const [templates, setTemplates] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [expandedTemplates, setExpandedTemplates] = useState({});
    const [evaluationResults, setEvaluationResults] = useState({});
    const [evaluationLoading, setEvaluationLoading] = useState(false);
    const [scanningTemplates, setScanningTemplates] = useState(new Set());
    const [hasSelectedTemplate, setHasSelectedTemplate] = useState(!!template);
    
    // Fetch templates on mount
    useEffect(() => {
      const fetchTemplates = async () => {
        setLoading(true);
        try {
          const urlParams = new URLSearchParams(window.location.search);
          const school = urlParams.get('school') || 'MARYLAND';
          const data = await getAllTemplates(school);
          setTemplates(data.templates || []);
          setLoading(false);
        } catch (err) {
          setError(err.message || 'Failed to load templates');
          setLoading(false);
        }
      };
      
      fetchTemplates();
    }, []);
    
    // Fetch evaluation results when templates are loaded
    useEffect(() => {
      const fetchEvaluationResults = async () => {
        if (templates.length === 0 || courseList.length === 0) return;
        
        setEvaluationLoading(true);
        try {
          const template_id_list = templates.map(t => String(t.id));
          const course_list = courseList;
          const schoolParam = school || 'MARYLAND';
          
          const results = await getEvaluateResult(template_id_list, course_list, schoolParam);
          setEvaluationResults(results);
        } catch (err) {
          console.error('Error fetching evaluation results:', err);
          // Don't show error to user, just continue without status
        } finally {
          setEvaluationLoading(false);
        }
      };
      
      fetchEvaluationResults();
    }, [templates, courseList, school]);
    
    // Handle scanning a specific template
    const handleScanTemplate = async (templateId, e) => {
      e.stopPropagation(); // Prevent template selection
      
      setScanningTemplates(prev => new Set([...prev, templateId]));
      
      try {
        const schoolParam = school || 'MARYLAND';
        
        // Call the new assessment API for each course
        // Pass program_name as None when in course mode, otherwise use displayProgramName
        const programName = isCourse ? null : displayProgramName;
        const results = await startAssessmentByTemplate(
          schoolParam,
          courseList,
          templateId,
          programName
        );
        
        // For now, we'll assume success means the template is ready
        // Update evaluation results to mark this template as ready for all courses
        const templateResults = {};
        courseList.forEach(course => {
          templateResults[course] = 'yes'; // Mark as ready
        });
        
        setEvaluationResults(prev => ({
          ...prev,
          [String(templateId)]: templateResults
        }));
        
        // Show success notification
        addNotification(`Assessment started for template`, 'success');
      } catch (err) {
        console.error('Error starting assessment:', err);
        addNotification('Failed to start assessment. Please try again.', 'error');
      } finally {
        setScanningTemplates(prev => {
          const newSet = new Set(prev);
          newSet.delete(templateId);
          return newSet;
        });
      }
    };
    
    // Function to check if a template has keywords and concepts generated
    const hasGeneratedContent = (template) => {
      if (!template.LG || template.LG.length === 0) return false;
      
      // Check if any learning goal or sub-learning goal has keywords or concepts
      return template.LG.some(lg => {
        // Check main learning goal
        const hasMainContent = (lg.keywords && lg.keywords.length > 0) || 
                              (lg.concepts && lg.concepts.length > 0);
        
        // Check sub learning goals
        const hasSubContent = lg.objectives && lg.objectives.some(obj => 
          (obj.keywords && obj.keywords.length > 0) || 
          (obj.concepts && obj.concepts.length > 0)
        );
        
        return hasMainContent || hasSubContent;
      });
    };

    // Calculate template status based on evaluation results
    const getTemplateStatus = (templateId) => {
      // First, find the actual template object to check for keywords/concepts
      const template = templates.find(t => t.id === templateId);
      
      // If template doesn't have keywords/concepts, it needs to generate them first
      if (template && !hasGeneratedContent(template)) {
        return { 
          status: 'No Keywords/Concepts', 
          disabled: true, 
          color: '#d69e2e',
          needsGeneration: true 
        };
      }
      
      const results = evaluationResults[String(templateId)];
      if (!results) return { status: 'unknown', disabled: false };
      
      const courseStatuses = courseList.map(course => results[course]);
      
      // Check if any course has "no" status
      if (courseStatuses.some(status => status === 'no')) {
        return { status: 'Not Ready', disabled: true, color: '#c53030' };
      }
      
      // Check if any course has "processing" status (and none have "no")
      if (courseStatuses.some(status => status === 'processing')) {
        return { status: 'Processing', disabled: true, color: '#916A1D' };
      }
      
      // If all courses have "yes" status
      if (courseStatuses.every(status => status === 'yes')) {
        return { status: 'Ready', disabled: false, color: '#2d7a3d' };
      }
      
      // Default case
      return { status: 'unknown', disabled: false };
    };
    
    // Toggle expansion of a template
    const toggleTemplateExpansion = (templateId, e) => {
      e.stopPropagation(); // Prevent the click from selecting the template
      setExpandedTemplates(prev => ({
        ...prev,
        [templateId]: !prev[templateId]
      }));
    };
    
    // Handle template selection with status validation
    const handleTemplateSelection = (templateItem) => {
      const templateStatus = getTemplateStatus(templateItem.id);
      if (templateStatus.disabled) {
        return; // Don't allow selection of disabled templates
      }
      onSelectTemplate(templateItem);
      setHasSelectedTemplate(true);
    };
    
    // Handle modal close - check if template was selected
    const handleModalClose = () => {
      if (!hasSelectedTemplate) {
        // No template was selected, redirect back to previous page
        onBack();
      } else {
        onClose();
      }
    };
    
    // Toggle expansion of sub learning goal
    const toggleSubLG = (templateId, lgIndex, objIndex) => {
      const key = `${templateId}-${lgIndex}-${objIndex}`;
      setExpandedSubLGs(prev => ({
        ...prev,
        [key]: !prev[key]
      }));
    };
    
    return (
      <div className="learning-goals-modal-overlay">
        <div className="learning-goals-modal select-learning-goals-modal">
          <div className="modal-header-lg">
            <h2>Select Learning Goals</h2>
            <button className="close-button-lg" onClick={handleModalClose}>×</button>
          </div>
          
          <div className="modal-content">
            {loading ? (
              <div className="loading">Loading templates...</div>
            ) : error ? (
              <div className="error-message">Error: {error}</div>
            ) : (
              <div className="templates-list">
                {templates.map((templateItem, templateIndex) => {
                  const templateStatus = getTemplateStatus(templateItem.id);
                  const isDisabled = templateStatus.disabled;
                  const isScanning = scanningTemplates.has(templateItem.id);
                  const showScanButton = templateStatus.status === 'Not Ready';
                  const needsGeneration = templateStatus.needsGeneration;
                  
                  return (
                    <div 
                      key={templateIndex} 
                      className={`template-item ${template && template.id === templateItem.id ? 'selected' : ''} ${isDisabled ? 'disabled' : ''}`}
                    >
                      <div className="template-row">
                        {needsGeneration ? (
                          <div className="generation-needed-message">
                            <span className="generation-icon">⚠️</span>
                            <span className="generation-text">
                              Please generate keywords and concepts first in the Accreditation Bank
                            </span>
                          </div>
                        ) : showScanButton ? (
                          <button
                            className={`scan-button ${isScanning ? 'scanning' : ''}`}
                            onClick={(e) => handleScanTemplate(templateItem.id, e)}
                            disabled={isScanning}
                            aria-label={`Scan template ${templateItem.name} for updates`}
                            title="Re-scan this template to check for status updates"
                          >
                            {isScanning ? (
                              <div className="scan-spinner" aria-hidden="true"></div>
                            ) : null}
                            <span className="scan-text">
                              {isScanning ? 'Scanning...' : 'Scan'}
                            </span>
                          </button>
                        ) : (templateStatus.status === 'Processing' || templateStatus.status === 'Ready') && (
                          <button
                            className={`scan-button rescan-button ${isScanning ? 'scanning' : ''}`}
                            onClick={(e) => handleScanTemplate(templateItem.id, e)}
                            disabled={isScanning}
                            aria-label={`Re-scan template ${templateItem.name} for updates`}
                            title="Re-scan this template to check for status updates"
                          >
                            {isScanning ? (
                              <div className="scan-spinner" aria-hidden="true"></div>
                            ) : null}
                            <span className="scan-text">
                              {isScanning ? 'Scanning...' : 'Rescan'}
                            </span>
                          </button>
                        )}
                        <div 
                          className="template-header" 
                          onClick={isDisabled ? undefined : () => handleTemplateSelection(templateItem)}
                          style={{ cursor: isDisabled ? 'not-allowed' : 'pointer' }}
                        >
                          <div className="template-name">
                            <h3>{templateItem.name}</h3>
                            {templateStatus.status !== 'unknown' && (
                              <div className="template-status">
                                <span 
                                  className={`status-badge ${templateStatus.status.toLowerCase().replace(' ', '-')}`}
                                  style={{ 
                                    backgroundColor: templateStatus.color,
                                    color: 'white'
                                  }}
                                >
                                  {evaluationLoading ? 'Checking...' : templateStatus.status}
                                </span>
                              </div>
                            )}
                          </div>
                          <div className="template-actions">
                            <button 
                              className="expand-template-button"
                              onClick={(e) => toggleTemplateExpansion(templateItem.id, e)}
                              aria-label={expandedTemplates[templateItem.id] ? "Collapse template" : "Expand template"}
                            >
                              {expandedTemplates[templateItem.id] ? "Collapse" : "Expand"}
                            </button>
                            <div className={`template-select ${template && template.id === templateItem.id ? 'selected-outline' : ''}`}>
                              <div className={`radio-circle ${template && template.id === templateItem.id ? 'selected' : ''} ${isDisabled ? 'disabled' : ''}`}>
                                {template && template.id === templateItem.id && !isDisabled && <div className="radio-inner"></div>}
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                      
                      {expandedTemplates[templateItem.id] && templateItem.LG && (
                        <div className="learning-goals-list">
                          {templateItem.LG.map((lg, lgIndex) => (
                            <div key={lgIndex} className="learning-goal-item">
                              <div className="lg-header">
                                <h4>{lg.title}</h4>
                              </div>
                              
                              <div className="lg-details">
                                <p className="lg-description">{lg.description}</p>
                                
                                {/* Keywords Section */}
                                {lg.keywords && lg.keywords.length > 0 && (
                                  <div className="lg-keywords-section">
                                    <h5>Keywords:</h5>
                                    <div className="lg-keywords-container">
                                      {lg.keywords.map((keyword, idx) => (
                                        <span key={idx} className="lg-keyword-tag">
                                          {keyword}
                                        </span>
                                      ))}
                                    </div>
                                  </div>
                                )}
                                
                                {/* Concepts Section */}
                                {lg.concepts && lg.concepts.length > 0 && (
                                  <div className="lg-concepts-section">
                                    <h5>Concepts:</h5>
                                    <ul className="lg-concepts-list">
                                      {lg.concepts.map((concept, idx) => (
                                        <li key={idx} className="lg-concept-item">
                                          {concept}
                                        </li>
                                      ))}
                                    </ul>
                                  </div>
                                )}
                                
                                {/* Sub Learning Goals Section */}
                                {lg.objectives && lg.objectives.length > 0 && (
                                  <div className="sub-learning-goals">
                                    <h5>Learning Objectives:</h5>
                                    <ul>
                                      {lg.objectives.map((obj, objIndex) => {
                                        const subLgKey = `${templateItem.id}-${lgIndex}-${objIndex}`;
                                        const isSubLGExpanded = expandedSubLGs[subLgKey];
                                        
                                        return (
                                          <li key={objIndex} className="sub-lg-item">
                                            <div 
                                              className={`sub-lg-header ${isSubLGExpanded ? 'expanded' : ''}`}
                                              onClick={() => toggleSubLG(templateItem.id, lgIndex, objIndex)}
                                            >
                                              <strong>{obj.sub_learning_goal_id}:</strong>&nbsp;&nbsp; {obj.description}
                                              
                                            </div>
                                            
                                            {isSubLGExpanded && (
                                              <div className="sub-lg-details">
                                                {/* Sub-objective Keywords */}
                                                {obj.keywords && obj.keywords.length > 0 && (
                                                  <div className="lg-sub-keywords-section">
                                                    <h6>Keywords:</h6>
                                                    <div className="lg-keywords-container">
                                                      {obj.keywords.map((keyword, kidx) => (
                                                        <span key={kidx} className="lg-keyword-tag sub-keyword">
                                                          {keyword}
                                                        </span>
                                                      ))}
                                                    </div>
                                                  </div>
                                                )}
                                                
                                                {/* Sub-objective Concepts */}
                                                {obj.concepts && obj.concepts.length > 0 && (
                                                  <div className="lg-sub-concepts-section">
                                                    <h6>Concepts:</h6>
                                                    <ul className="lg-concepts-list sub-concepts">
                                                      {obj.concepts.map((concept, cidx) => (
                                                        <li key={cidx} className="lg-concept-item sub-concept">
                                                          {concept}
                                                        </li>
                                                      ))}
                                                    </ul>
                                                  </div>
                                                )}
                                              </div>
                                            )}
                                          </li>
                                        );
                                      })}
                                    </ul>
                                  </div>
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
          
          <div className="modal-footer">
            <button className="select-button" onClick={handleModalClose}>Close</button>
          </div>
        </div>
      </div>
    );
  };

  // Show CourseManagement component if state is true
  if (showCourseManagement) {
    return (
      <CourseManagement 
        onBack={() => setShowCourseManagement(false)}
        programName={programName}
        templateId={selectedTemplate?.id}
        school={school}
      />
    );
  }

  return (
    <div className="assurance-report">
      {showLearningGoalsModal && (
        <SelectLearningGoalsModal 
          onClose={() => setShowLearningGoalsModal(false)}
          template={templateData}
          onSelectTemplate={handleSelectTemplate}
        />
      )}
      
      <div className="report-header">
        <div></div>
        
        <div className="header-actions">
          <button className="back-button" onClick={onBack}>
            <span className="back-icon">←</span>
            Back
          </button>
        </div>
      </div>
      
      <div className="title-row">
        <h1 className="report-title">{isCourse ? "Course" : ""} Assurance of Learning Report</h1>
        
        <div className="title-actions">
          <div className="export-container" ref={exportRef}>
            <button className="export-button" onClick={handleExportClick}>
              Export
            </button>
            
            {showExportDropdown && (
              <ExportDropdown 
                isVisible={showExportDropdown}
                fileType={fileType}
                exportSelections={exportSelections}
                showFileTypeDropdown={showFileTypeDropdown}
                onCancel={() => setShowExportDropdown(false)}
                onExport={handleExportOption}
                onFileTypeClick={handleFileTypeClick}
                onFileTypeSelect={selectFileType}
                onSelectionChange={handleExportSelectionChange}
                years={years}
              />
            )}
          </div>
          
          <button 
            className="report-generation-button"
            onClick={handleReportGenerationClick}
          >
            <span className="material-symbols-outlined">download</span>
            Report Generation
          </button>
          
          <button 
            className="review-button attention-button"
            onClick={() => setShowLearningGoalsModal(true)}
          >
            Review Learning Goals (LGs)
            <span className="click-indicator"></span>
          </button>
        </div>
      </div>
      
      {isLoading ? (
        <div className="loading-message">Loading template data...</div>
      ) : error ? (
        <div className="error-message">
          {error}
          <button onClick={() => window.location.href = '/accreditation-bank'} className="navigate-button">
            Go to Accreditation Bank
          </button>
        </div>
      ) : (
        <>
      <div className="program-details">
        <div className="detail-item">
          <span className="detail-label">
            <img src="/school.svg" alt={isCourse ? "Course" : "Program"} className="detail-icon" />
            {isCourse ? "Course" : "Program"}
          </span>
          <span className="detail-value">
            {displayProgramName}
          </span>
        </div>
        
        <div className="detail-item">
          <span className="detail-label">
            <img src="/school.svg" alt="School" className="detail-icon" />
            School
          </span>
          <span className="detail-value">
            {school}
          </span>
        </div>
      </div>
      
      <div className="tab-content-area">
        <div className="report-tabs">
          <button 
            className={`tab-button ${activeTab === 'Curriculum Mapping' ? 'active' : ''}`}
            onClick={() => handleTabChange('Curriculum Mapping')}
          >
            <img src="/curriculum-mapping.svg" alt="Curriculum" className="tab-icon" />
            Curriculum Mapping
          </button>
          
          <button 
            className={`tab-button ${activeTab === 'Course Alignment Overview' ? 'active' : ''}`}
            onClick={() => handleTabChange('Course Alignment Overview')}
          >
            <img src="/course-alignment.svg" alt="Course" className="tab-icon" />
            Course Alignment Overview
          </button>
          
          {/* <button 
            className={`tab-button ${activeTab === 'Indirect Method Summary' ? 'active' : ''} disabled`}
            onClick={() => {}}
            disabled
          >
            <img src="/indirect-method.svg" alt="Method" className="tab-icon" />
            Indirect Method Summary
          </button>  */}
          <button 
            className={`tab-button ${activeTab === 'Indirect Method Summary' ? 'active' : ''}`}
            onClick={() => handleTabChange('Indirect Method Summary')}
          >
            <img src="/indirect-method.svg" alt="Method" className="tab-icon" />
            Indirect Method Summary
          </button> 
          
          <button 
            className={`tab-button ${activeTab === 'Recommendations' ? 'active' : ''}`}
            onClick={() => handleTabChange('Recommendations')}
          >
            <img src="/recommendation.svg" alt="Recommendations" className="tab-icon" />
            Recommendations
          </button>
          
          <button 
            className={`tab-button ${activeTab === 'Micro-credentials' ? 'active' : ''}`}
            onClick={() => handleTabChange('Micro-credentials')}
          >
            <img src="/credentials.svg" alt="Micro-credentials" className="tab-icon" />
            Micro-credentials
          </button>
          
          {!isCourse && (
            <button 
              className={`tab-button ${activeTab === 'Career Assessment' ? 'active' : ''}`}
              onClick={() => handleTabChange('Career Assessment')}
            >
              <img src="/briefcase.svg" alt="Career Assessment" className="tab-icon" />
              Career Assessment
            </button>
          )}
          
                       {/* Progress Trends tab - hidden for now
                       <button
                 className={`tab-button ${activeTab === 'Progress Trends' ? 'active' : ''}`}
                 onClick={() => handleTabChange('Progress Trends')}
               >
                 <img src="/history-24.svg" alt="Progress Trends" className="tab-icon" />
                 Progress Trends
               </button>
                       */}
        </div>
        
        {renderTabContent()}
      </div>
        </>
      )}
      
      <NotificationContainer 
        notifications={notifications} 
        removeNotification={removeNotification} 
      />
      
      {/* PDF Preview Modal */}
      {showPdfModal && (
        <div className="pdf-modal-overlay" onClick={handleClosePdfModal}>
          <div className="pdf-modal" onClick={(e) => e.stopPropagation()}>
            <div className="pdf-modal-header">
              <div className="pdf-title">{selectedPdfTitle}.pdf</div>
              <button className="pdf-download-btn" onClick={handleDownloadPdf}>
                Download
              </button>
              <button className="pdf-close-btn" onClick={handleClosePdfModal}>
                <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                  <path d="M9 3L3 9M3 3L9 9" stroke="#8E8E8E" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </button>
            </div>
            <div className="pdf-modal-content">
              {isLoadingPdf ? (
                <div className="pdf-loading">
                  <div className="loading-icon">⏳</div>
                  <h4>Loading PDF...</h4>
                  <p>Preparing PDF for preview...</p>
                </div>
              ) : !pdfPreviewError && pdfBlobUrl ? (
                <iframe
                  key={`pdf-preview-${selectedPdfTitle}`}
                  src={pdfBlobUrl}
                  title={selectedPdfTitle}
                  className="pdf-iframe"
                  frameBorder="0"
                  onError={() => {
                    console.log('PDF preview failed to load');
                    setPdfPreviewError(true);
                  }}
                  onLoad={() => {
                    console.log('PDF preview loaded successfully');
                  }}
                />
              ) : (
                <div className="preview-error">
                  <div className="error-icon">📄</div>
                  <h4>Preview not available</h4>
                  <p>Unable to load PDF preview.</p>
                  <button 
                    className="pdf-open-btn" 
                    onClick={() => window.open(selectedPdfUrl, '_blank')}
                  >
                    Open in New Tab
                  </button>
                </div>
              )}
              <div className="pdf-fallback">
                <p>Can't see the preview? 
                  <button 
                    className="preview-link-btn" 
                    onClick={() => window.open(selectedPdfUrl, '_blank')}
                  >
                    Click here to view the PDF
                  </button>
                </p>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AssuranceReport; 